import { useContext, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import { DigitalAssetContext } from "../../contexts/continente-credentials/DigitalAssetContext";

import callCenter from "@/assets/images/call-center.svg";

import CredentialsHeader from "@/components/header/Header";
import CredentialsFooter from "@/components/footer/Footer";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import FreshStartButton from "@/components/fresh-start-button/fresh-start-button";
import SimpleErrorMessage from "@/components/error-message/new-error-message";
import { Button } from "@/components/ui/button";

import { FreshStartOrigin } from "@/config/FreshStartOriginConfig";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { FormError } from "@/models/continente-credentials/common/FormError";
import { FreshStartRequest, FreshStartResponse } from "@/models/shared/freshStart/FreshStartModels";

import { tryCatch } from "@/utils/try-catch";
import { genericService } from "@/services/GenericService";

type BlockedInputProps = {
  faqAnchor: string;
  freshStartOrigin?: FreshStartOrigin;
};

export default function Blocked(props: BlockedInputProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<any>();

  const { handleResponse } = useApiResponseHandler();

  const [loading, setLoading] = useState(false);

  const {
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormError>({ mode: "onSubmit", reValidateMode: "onSubmit" });

  const digitalAssetContext = useContext(DigitalAssetContext);

  const phoneFreshStartEligible = location.state?.properties?.phoneFreshStartEligible === true;

  const goToLogin = () => {
    history.push(
      `/auth/${digitalAssetContext.digitalAsset.clientId}`
    );
  };

  const freshStart = async () => {
    clearErrors();
    setLoading(true);

    const { data: response, error } = await tryCatch(genericService.post<FreshStartResponse, FreshStartRequest>(
      "api/phonenumber/login/fresh-start",
      {
        origin: props.freshStartOrigin
      }
    ));

    handleResponse(response, { setError }, error);
    setLoading(false);
  };

  return (
    <div className="flex flex-col gap-12 pb-2">
      <CredentialsHeader
        title={t("blocked_page.header_title")}
        subtitle={t("blocked_page.subtitle")}
        showLoading={loading}
      />
      <div className="flex justify-center items-center">
        <img src={callCenter} alt="Callcenter image" />
      </div>
      {errors.submitError?.message && (
        <SimpleErrorMessage
          id="blocked__error-message"
          error={errors.submitError.message}
        />
      )}
      <div className="flex flex-col gap-4">
        {props.freshStartOrigin && phoneFreshStartEligible && (
          <FreshStartButton onClick={freshStart} />
        )}
        <NeedHelpButton anchor={props.faqAnchor} />
      </div>
      <Button
        type="button"
        onClick={async () => {
          goToLogin();
        }}
        variant={"secondary"}
      >
        {t("common.back")}
      </Button>
      <div
        role="note"
        aria-label={t("accessibility.signin.icons")}
        className="flex flex-col">
        <CredentialsFooter />
      </div>
    </div>
  );
}
